import {of as observableOf, combineLatest as observableCombineLatest, Observable} from 'rxjs';

import {first, catchError, startWith} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';

@Component({
  selector: 'attribute-flow-destination',
  styles: ['mat-form-field { width: 100%; }'],
  template: `
    <mat-form-field>
      <input
        #input
        type="text"
        matInput
        (focus)="inputFocus()"
        (blur)="inputBlur()"
        [placeholder]="placeholder"
        [formControl]="formControl"
        [matAutocomplete]="auto"
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="optionSelected($event)"
        autoActiveFirstOption
      >
        @for (option of filteredOptions | async; track option) {
          <mat-option [value]="option">
            {{ option }}
          </mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AttributeFlowDestinationComponent implements OnInit {
  formControl = new UntypedFormControl();

  @Output() valueChange = new EventEmitter<string>();

  @Output() onError = new EventEmitter<{err: any; caught: any}>();

  @Input() set value(value: string) {
    this.formControl.setValue(value);
    this.savedValue = value;
  }

  @Input() options: Observable<string[]>;

  @Input() placeholder: string;

  @ViewChild('input', {static: true}) input: ElementRef;

  @Input() set disabled(disabled: boolean) {
    if (disabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  private savedValue: string;
  private inputInvalidValue: boolean = false;
  filteredOptions: Observable<string[]>;

  ngOnInit(): void {
    this.filteredOptions = observableCombineLatest(
      this.options,
      this.formControl.valueChanges.pipe(startWith(null)),
      (attributes: string[], inputValue: string) => {
        if (inputValue == null) {
          return attributes;
        }

        if (typeof inputValue !== 'string') return [];

        const filterValue = inputValue.toLowerCase();
        return attributes ? attributes.filter(a => a.toLowerCase().indexOf(filterValue) === 0) : [];
      },
    );
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;
    this.inputInvalidValue = false;

    this.options
      .pipe(
        catchError((err, caught) => {
          this.onError.emit({err: err, caught: caught});
          return observableOf(null);
        }),
        first(),
      )
      .subscribe((attributes: string[]) => {
        if (attributes.indexOf(value) >= 0) {
          this.savedValue = value;
          this.valueChange.emit(value);
          this.inputInvalidValue = false;
          this.formControl.setValue(value);
        } else {
          this.inputInvalidValue = true;
          this.formControl.setValue(this.savedValue);
        }
        this.input.nativeElement.blur();
      });
  }

  inputBlur(): void {
    if (this.inputInvalidValue) {
      setTimeout(() => {
        if (this.inputInvalidValue) {
          this.formControl.setValue(this.savedValue);
          this.inputInvalidValue = false;
        }
      }, 300);
    }
  }

  inputFocus(): void {
    this.inputInvalidValue = true;
    this.formControl.setValue('');
  }
}
